<template>
    <section :id="id" class="article-list">
        <div ref="article-header" class="inner">
            <h2 class="h4">{{ title }}</h2>
            <div v-if="display.toolbar !== 'false' || display.tagbar !== 'false'" class="search-bar">
                <div v-if="display.tagbar !== 'false' && display.tags?.length > 0" class="tags">
                    <button
                        class="tag"
                        :class="{
                            'selected': selectedTags.length === display.tags.length,
                        }"
                        @click="selectedTags = [...display.tags]"
                    >
                        {{ i18n.t('viewAll') }}
                    </button>
                    <button
                        v-for="(item, key) in display.tags"
                        :key="`tag-${item}-${key}`"
                        class="tag"
                        :class="{
                            'selected': selectedTags.length < display.tags.length && selectedTags.includes(item),
                        }"
                        @click="toggleTag(item)"
                    >
                        {{ item.name }}
                    </button>
                </div>
                <DSInput
                    v-if="display.toolbar !== 'false'"
                    v-model="searchParam"
                    class="search-input"
                    :label="i18n.t('searchPlaceholder')"
                />
                <transition name="fade">
                    <button
                        v-if="searchParam.length > 0"
                        class="reset"
                        @click="searchParam = ''"
                    >
                        <DSFontIcon code="close" icon-style="line" size="l" />
                    </button>
                </transition>
            </div>
            <BlocksCLCards
                v-if="displayType === 'cards'"
                tag="div"
                v-bind="computedProps"
            />
            <BlocksCLQuickLinks
                v-else-if="displayType === 'quicklinks'"
                tag="div"
                v-bind="computedProps"
            />
        </div>
        <DSPaginationControls
            v-if="pagination.pageCount > 1"
            v-bind="pagination"
            @update:model-value="value => currentPage = value"
        />
    </section>
</template>
<script setup>
import { ref, computed, useI18n, watch, useNuxtApp, useCmsProxy, useTemplateRef } from '#imports';
import { DateTime } from 'luxon';

const { $paths } = useNuxtApp();
const selectedTags = ref([]);
const searchParam = ref('');
const debounce = ref(null);
const articleHeader = useTemplateRef('article-header');

const props = defineProps({
    id: {
        type: String,
        required: false,
        default: '',
    },
    contentType: {
        type: String,
        required: false,
        default: 'articleList',
    },
    createdAt: {
        type: String,
        required: false,
        default: '',
    },
    updatedAt: {
        type: String,
        required: false,
        default: '',
    },
    internal: {
        type: String,
        required: false,
        default: '',
    },
    title: {
        type: String,
        required: false,
        default: '',
    },
    cards: {
        type: Array,
        required: false,
        default: () => [],
    },
    display: {
        type: Object,
        required: false,
        default: () => ({
            tags: [],
        }),
    },
    articles: {
        type: Object,
        required: false,
        default: () => ({
            results: [],
            total: 0,
            itemsPerPage: 9,
        }),
    },
});

const currentArticles = ref(props.articles);

if (props.display.tags) {
    selectedTags.value = [...props.display.tags];
}

const i18n = useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            searchPlaceholder: 'Haku',
            more: 'Lue lisää',
            viewAll: 'Näytä kaikki',
        },
        'en-EU': {
            searchPlaceholder: 'Search',
            more: 'Read more',
            viewAll: 'View all',
        },
        'sv-SE': {
            searchPlaceholder: 'Sök',
            more: 'Läs mer',
            viewAll: 'Se alla',
        },
        'no-NO': {
            searchPlaceholder: 'Søk',
            more: 'Les mer',
            viewAll: 'Se alle',
        },
    },
});

const currentPage = ref(1);

const pagination = computed(() => ({
    modelValue: currentPage.value,
    pageCount: Math.ceil(currentArticles.value?.total / props.articles?.itemsPerPage),
    scrollTop: articleHeader?.value?.offsetTop - window?.document?.querySelector('header')
        ?.getBoundingClientRect()?.height || 0,
}));

const load = () => {
    if (debounce.value) clearTimeout(debounce.value);

    debounce.value = setTimeout(async () => {
        const res = await useCmsProxy($paths.getEndpoint('search'), {
            method: 'post',
            body: {
                textQuery: searchParam.value,
                page: currentPage.value,
                limit: props.articles.itemsPerPage,
                sort: {
                    sortBy: 'created',
                    sortDirection: 'DESC',
                },
                filter: {
                    category: 'Article',
                    tags: selectedTags.value.map(tag => tag.value),
                },
            },
        });

        currentArticles.value = res ?? {
            results: [],
            total: 0,
            itemsPerPage: props.articles.itemsPerPage,
        };
    }, 500);
};

watch(() => currentPage.value, () => {
    load();
});

watch(() => searchParam.value.length || selectedTags.value.length, () => {
    currentPage.value = 1;
    load();
});

const toggleTag = selectedTag => {
    if (selectedTags.value.length === props.display.tags.length) {
        selectedTags.value = [selectedTag];
    } else if (selectedTags.value.includes(selectedTag)) {
        selectedTags.value.splice(selectedTags.value.indexOf(selectedTag), 1);

        if (selectedTags.value.length === 0) {
            selectedTags.value = [...props.display.tags];
        }
    } else {
        selectedTags.value.push(selectedTag);
    }
};

const displayType = computed(() => {
    if (props.display.mode) {
        return props.display.mode;
    }

    return 'cards';
});

const computedProps = computed(() => ({
    id: props.id,
    contentType: props.contentType,
    createdAt: props.createdAt,
    updatedAt: props.updatedAt,
    internal: props.internal,
    title: undefined,
    display: {
        ...props.display,
        ...(displayType.value === 'cards' ? {
            background: 'secondary',
        } : {
            background: 'fullbg',
        }),
    },
    countOverride: currentArticles.value.results.length > 3 ? 3 : '',
    cards: currentArticles.value.results.map(article => ({
        ...article.content,
        text: article.content.description,
        byline: `${DateTime.fromISO(article.content.created)
            .toFormat('d.M.yyyy')}${article.content.byline ? ` | ${article.content.byline}` : ''}`,
        links: [{
            title: i18n.t('more'),
            url: {
                contentType: 'link',
                type: 'internal',
                url: article.localizedPagePaths[i18n.locale.value],
            },
        }],
    })),
}));


</script>
<style scoped>
.article-list {
  padding-bottom: 4rem;
}
.article-list {
  display: grid;
  max-width: var(--desktop-max-width);
  width: 100%;
  margin: 0 auto;
}
.article-list .inner {
  max-width: var(--page-max-width);
  margin: 0 auto;
  padding-left: 1.5rem;
  width: 100%;
  padding-right: 1.5rem;
}

.search-bar {
  margin-block: 1.5rem;
  display: flex;
  align-items: center;
}
.search-bar .search-input {
  width: 50%;
}
@media (max-width: 767px) {
  .search-bar {
    flex-wrap: wrap;
  }
  .search-bar .search-input, .search-bar .reset {
    margin: 1rem 0;
    width: 100%;
  }
}

.tags {
  margin-bottom: -0.5rem;
}

.tag {
  transition: all 0.2s ease-in-out;
  margin-right: 0.75rem;
  margin-bottom: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  border: 2px solid var(--color-secondary-50);
  letter-spacing: 0.025em;
}
.tag.selected {
  background: var(--color-secondary-50);
}

.reset {
  position: relative;
  z-index: 1;
  justify-self: end;
  margin-right: 0.5rem;
  display: inline-flex;
  align-items: flex-start;
}

.search-input {
  margin-bottom: 0;
}

.ds-pagination-controls {
  display: inline-flex;
  justify-self: center;
  margin-top: 1rem;
}

.search-input, .reset {
  grid-column-start: 2;
  grid-row-start: 1;
}
</style>
